import { useCallback, useEffect, useState } from 'react';
import useLicenseList from '../../../license-management/hooks/useLicenseList';
import { DEFAULT_OBJECT_PERMISSIONS, DEFAULT_GENERAL_PERMISSIONS } from '../../utils/constants';
import {
  organisationNeedsLicenseProfileResolve,
  patchOrganisationWithLicenseProfile,
} from '../../utils/licenseProfileResolver';
import { useCloneProfilePermissions } from '../../hooks/useCloneProfilePermissions';
import { useWizard } from '../../hooks/WizardProvider';
import { getLicenseProfileRef, suggestProfileName } from './profileStepUtils';

export default function useProfileStep() {
  const {
    draft,
    setDraft,
    steps,
    stepErrors,
    currentStep,
    organisationId,
    profileId,
    sessionId,
    isSaving,
    createProfileNow,
  } = useWizard();

  const [useManualTemplate, setUseManualTemplate] = useState(false);
  const { licenses, fetchLicenses } = useLicenseList();

  const profileMeta = draft.profile_users ?? {
    clone_from_profile_id: null,
    profile_name: '',
    permissions_source: 'license',
  };

  const licenseProfile = getLicenseProfileRef(draft.organisation);
  const permissionsSource = profileMeta.permissions_source ?? 'license';
  const profileCreated = profileId != null;

  const { permissionsLoading, permissionsError, permissionsLoaded } = useCloneProfilePermissions(
    profileMeta.clone_from_profile_id,
    setDraft,
    draft.objectPermissions
  );

  const showPermissions =
    !!profileMeta.clone_from_profile_id && permissionsLoaded && !permissionsLoading;

  useEffect(() => {
    void fetchLicenses();
  }, [fetchLicenses]);

  useEffect(() => {
    if (licenses.length === 0) return;
    if (!organisationNeedsLicenseProfileResolve(draft.organisation)) return;

    setDraft((prev) => {
      const patched = patchOrganisationWithLicenseProfile(prev.organisation, licenses);
      if (
        patched.license_permission_profile_id === prev.organisation.license_permission_profile_id &&
        patched.license_permission_profile_name === prev.organisation.license_permission_profile_name
      ) {
        return prev;
      }
      return { ...prev, organisation: patched };
    });
  }, [draft.organisation?.license_id, draft.organisation?.license_permission_profile_id, licenses, setDraft]);

  useEffect(() => {
    if (profileCreated) return;

    const licenseProfileId = licenseProfile?.id;
    if (!licenseProfileId) return;

    const currentClone = profileMeta.clone_from_profile_id;
    const source = profileMeta.permissions_source ?? 'license';
    const shouldApplyLicenseTemplate =
      !currentClone ||
      (source === 'license' && String(currentClone) !== String(licenseProfileId));

    if (!shouldApplyLicenseTemplate) {
      if (!profileMeta.profile_name?.trim()) {
        const suggested = suggestProfileName(
          draft.organisation?.name,
          draft.organisation?.license_alias
        );
        if (suggested) {
          setDraft((prev) => ({
            ...prev,
            profile_users: {
              ...prev.profile_users,
              profile_name: suggested,
            },
          }));
        }
      }
      return;
    }

    const suggestedName = suggestProfileName(
      draft.organisation?.name,
      draft.organisation?.license_alias
    );

    setDraft((prev) => ({
      ...prev,
      profile_users: {
        clone_from_profile_id: prev.profile_users?.clone_from_profile_id ?? null,
        profile_name: prev.profile_users?.profile_name ?? '',
        permissions_source: prev.profile_users?.permissions_source ?? 'license',
        ...prev.profile_users,
        clone_from_profile_id: licenseProfileId,
        permissions_source: 'license',
        profile_name: prev.profile_users?.profile_name?.trim() || suggestedName,
      },
    }));
    setUseManualTemplate(false);
  }, [
    draft.organisation?.license_alias,
    draft.organisation?.name,
    licenseProfile?.id,
    profileCreated,
    profileMeta.clone_from_profile_id,
    profileMeta.permissions_source,
    profileMeta.profile_name,
    setDraft,
  ]);

  const showCloneDropdown = useManualTemplate || permissionsSource === 'manual' || !licenseProfile;

  const updateProfileMeta = useCallback(
    (field, value) => {
      setDraft((prev) => ({
        ...prev,
        profile_users: {
          clone_from_profile_id: prev.profile_users?.clone_from_profile_id ?? null,
          profile_name: prev.profile_users?.profile_name ?? '',
          permissions_source: prev.profile_users?.permissions_source ?? 'license',
          ...prev.profile_users,
          [field]: value,
        },
      }));
    },
    [setDraft]
  );

  const enableManualTemplate = useCallback(() => {
    setUseManualTemplate(true);
  }, []);

  const handleCloneProfileChange = useCallback(
    (selected) => {
      const item = Array.isArray(selected) ? selected[0] : selected;
      setDraft((prev) => ({
        ...prev,
        profile_users: {
          clone_from_profile_id: prev.profile_users?.clone_from_profile_id ?? null,
          profile_name: prev.profile_users?.profile_name ?? '',
          permissions_source: 'manual',
          ...prev.profile_users,
          clone_from_profile_id: item?.id ?? null,
        },
      }));
      setUseManualTemplate(true);
    },
    [setDraft]
  );

  const resetToLicenseTemplate = useCallback(() => {
    if (!licenseProfile?.id) return;

    setDraft((prev) => ({
      ...prev,
      objectPermissions: DEFAULT_OBJECT_PERMISSIONS.map((p) => ({ ...p })),
      generalPermissions: DEFAULT_GENERAL_PERMISSIONS.map((p) => ({ ...p })),
      profile_users: {
        ...prev.profile_users,
        clone_from_profile_id: licenseProfile.id,
        permissions_source: 'license',
      },
    }));
    setUseManualTemplate(false);
  }, [licenseProfile?.id, setDraft]);

  return {
    draft,
    setDraft,
    steps,
    stepErrors,
    currentStep,
    organisationId,
    profileId,
    sessionId,
    isSaving,
    createProfileNow,
    profileMeta,
    licenseProfile,
    permissionsSource,
    profileCreated,
    permissionsLoading,
    permissionsError,
    permissionsLoaded,
    showPermissions,
    showCloneDropdown,
    useManualTemplate,
    setUseManualTemplate,
    updateProfileMeta,
    handleCloneProfileChange,
    resetToLicenseTemplate,
    enableManualTemplate,
  };
}
